import { useState, useEffect } from "react";
import { CT } from "@/content";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth, API } from "@/App";
import axios from "axios";
import { PdfThumbnail } from "@/components/PdfThumbnail";
import { HeroCarousel } from "@/components/HeroCarousel";
import { FeatureCardsGrid } from "@/components/FeatureCardsGrid";
import { isIOS } from "@/lib/utils";
import { 
  FileText, 
  ArrowRight,
  ExternalLink,
  Play,
  X,
  Zap,
  Bot,
  Download
} from "lucide-react";
import { motion } from "framer-motion";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [heroSlides, setHeroSlides] = useState([{ id: "default", url: "/hero.jpg", type: "image" }]);
  const [heroInterval, setHeroInterval] = useState(5);
  const [gallery, setGallery] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/homepage`).then(res => {
      if (res.data.hero_slides?.length) setHeroSlides(res.data.hero_slides);
      if (res.data.hero_interval) setHeroInterval(res.data.hero_interval);
      if (res.data.gallery) setGallery(res.data.gallery);
    }).catch(() => {});
  }, []);

  // Helper to get redirect path based on role
  const getRedirectPath = (role) => {
    if (role === "admin") return "/admin";
    if (role === "teacher") return "/teacher";
    return "/student";
  };

  const handleGetStarted = () => {
    if (user) {
      navigate(getRedirectPath(user.role));
    } else {
      navigate("/login");
    }
  };


  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="TheMatheMind 360" className="h-9 sm:h-10 rounded-lg" />
              <div className="flex flex-col">
                <span className="text-lg sm:text-xl font-bold text-foreground font-['Manrope'] leading-tight whitespace-nowrap">TheMatheMind <span className="text-amber-600">360</span></span>
                <span className="text-[10px] text-muted-foreground leading-tight tracking-widest uppercase">A 360&deg; Learning Platform</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <Button 
                  data-testid="dashboard-btn"
                  onClick={() => navigate(getRedirectPath(user.role))}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6"
                >
                  Go to Dashboard
                </Button>
              ) : (
                <Button 
                  data-testid="login-btn"
                  onClick={() => navigate("/login")}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 hero-pattern">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground mb-6 font-['Manrope']">
              <CT k="landing_hero_line1" />
              <span className="text-primary"> <CT k="landing_hero_highlight" trim /></span>
              <br /><CT k="landing_hero_line2" />
            </h1>
            <CT k="landing_hero_subtitle" block className="text-lg md:text-xl text-muted-foreground mb-8 max-w-4xl mx-auto leading-relaxed" />
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 justify-center items-center">
              <Button 
                data-testid="worksheet-generator-btn"
                size="lg"
                variant="outline"
                onClick={() => navigate("/worksheet-generator")}
                className="w-full sm:w-auto max-w-full whitespace-normal text-center h-auto min-h-12 py-2 rounded-full px-6 sm:px-8 text-base font-medium"
              >
                <FileText className="mr-2 w-5 h-5" />
                Abacus Free Worksheet Generator
              </Button>
              <Button 
                data-testid="mathbot-btn"
                size="lg"
                variant="outline"
                onClick={() => navigate("/login?redirect=/ai-tutor")}
                className="w-full sm:w-auto max-w-full whitespace-normal text-center h-auto min-h-12 py-2 rounded-full px-6 sm:px-8 text-base font-medium"
              >
                <Bot className="mr-2 w-5 h-5" />
                MathBot - AI-powered Maths Brain Platform
              </Button>
              <Button 
                data-testid="arena-btn"
                size="lg"
                onClick={() => navigate("/play/arena")}
                className="w-full sm:w-auto max-w-full whitespace-normal text-center h-auto min-h-12 py-2 rounded-full px-6 sm:px-8 text-base font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:opacity-90"
              >
                <Zap className="mr-2 w-5 h-5" />
                Times Tables Arena — Play Free
              </Button>
            </div>
          </motion.div>

          {/* Hero Image - Clickable */}
          <motion.div 
            className="mt-16 relative"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <HeroCarousel slides={heroSlides} interval={heroInterval} onOpen={setLightbox} />
          </motion.div>

          {/* Media Gallery */}
          {gallery.length > 0 && (
            <motion.div
              className="mt-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="media-gallery">
                {gallery.map((item) => (
                  <div
                    key={item.id}
                    className="relative rounded-xl overflow-hidden cursor-pointer group aspect-video bg-muted"
                    onClick={() => (item.type === "pdf" && isIOS() ? window.open(item.url, "_blank", "noopener") : setLightbox(item))}
                    data-testid={`gallery-item-${item.id}`}
                  >
                    {item.type === "video" ? (
                      <>
                        <video src={item.url} className="w-full h-full object-cover" muted preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                            <Play className="w-5 h-5 text-foreground ml-0.5" />
                          </div>
                        </div>
                      </>
                    ) : item.type === "pdf" ? (
                      <PdfThumbnail url={item.url} filename={item.filename} />
                    ) : (
                      <img src={item.url} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    )}
                    {item.type === "pdf" && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity"
                        title="Open PDF in new tab"
                        data-testid={`gallery-open-${item.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5 text-foreground" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Fullscreen Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={() => setLightbox(null)} data-testid="lightbox">
          <button className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" onClick={() => setLightbox(null)} data-testid="lightbox-close">
            <X className="w-6 h-6 text-white" />
          </button>
          {lightbox.type === "pdf" && (
            <a
              href={lightbox.url}
              download={lightbox.filename || "document.pdf"}
              onClick={(e) => e.stopPropagation()}
              className="absolute top-4 right-16 z-10 h-10 px-4 rounded-full bg-white/10 hover:bg-white/20 flex items-center gap-2 text-white text-sm font-medium transition-colors"
              data-testid="lightbox-download"
            >
              <Download className="w-4 h-4" /> Download
            </a>
          )}
          <div className="max-w-5xl max-h-[90vh] w-full mx-4" onClick={(e) => e.stopPropagation()}>
            {lightbox.type === "video" ? (
              <video src={lightbox.url} controls autoPlay className="w-full max-h-[90vh] rounded-lg" data-testid="lightbox-video" />
            ) : lightbox.type === "pdf" ? (
              <iframe src={lightbox.url} title={lightbox.filename || "PDF document"} className="w-full h-[90vh] rounded-lg bg-white" data-testid="lightbox-pdf" />
            ) : (
              <img src={lightbox.url || lightbox} alt="" className="w-full max-h-[90vh] object-contain rounded-lg" data-testid="lightbox-image" />
            )}
          </div>
        </div>
      )}

      {/* Features Section */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <FeatureCardsGrid />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4 font-['Manrope']">
              Ready to Learn, Practice & Achieve?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join TheMatheMind and start your advanced learning journey today.
            </p>
          </motion.div>

          {/* Official Website Link Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-12"
          >
            <Card className="bg-primary/5 border-primary/20 max-w-xl mx-auto">
              <CardContent className="p-6">
                <p className="text-muted-foreground text-sm mb-4">
                  Visit our official website to view and search for all available courses.
                </p>
                <a
                  href="https://www.themathemind.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-full font-medium hover:bg-primary/90 transition-colors"
                  data-testid="official-website-link"
                >
                  Visit www.themathemind.com
                  <ExternalLink className="w-4 h-4" />
                </a>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="TheMatheMind 360" className="h-8 rounded-lg" />
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-foreground font-['Manrope'] leading-tight">TheMatheMind <span className="text-amber-600">360</span></span>
              <span className="text-[10px] text-muted-foreground leading-tight">Where Learning Meets Innovation</span>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <a 
              href="https://www.themathemind.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Official Website
            </a>
            <p className="text-sm text-muted-foreground">
              © 2026 TheMatheMind. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
